import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, User, Clock, Lock, CheckCircle, Heart, Edit2, Trash2 } from 'lucide-react';
import { ICONS } from '@/components/IconPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], average_rating: 0, total_reviews: 0 });
  const [inWishlist, setInWishlist] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    loadCourse();
    loadReviews();
    checkEnrollment();
    checkWishlist();
    checkAdminStatus();
    
    // Check for enrollment success from payment
    if (searchParams.get('enrollment') === 'success') {
      toast.success('Successfully enrolled in course!');
      checkEnrollment();
    }
  }, [id]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const loadCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const checkEnrollment = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}/check-enrollment`, {
        withCredentials: true
      });
      setEnrolled(response.data.enrolled);
    } catch (error) {
      // Not logged in or not enrolled
      setEnrolled(false);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const checkWishlist = async () => {
    try {
      const response = await axios.get(`${API}/student/wishlist`, {
        withCredentials: true
      });
      setInWishlist(response.data.some(c => c.id === id));
    } catch (error) {
      setInWishlist(false);
    }
  };

  const toggleWishlist = async () => {
    try {
      if (inWishlist) {
        await axios.delete(`${API}/wishlist/${id}`, { withCredentials: true });
        toast.success('Removed from wishlist');
        setInWishlist(false);
      } else {
        await axios.post(`${API}/wishlist/${id}`, {}, { withCredentials: true });
        toast.success('Added to wishlist!');
        setInWishlist(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  const submitReview = async () => {
    if (userRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await axios.post(
        `${API}/courses/${id}/review`,
        null,
        {
          params: { rating: userRating, review_text: userReview },
          withCredentials: true
        }
      );
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setUserRating(0);
      setUserReview('');
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review.id);
    setEditRating(review.rating);
    setEditText(review.review_text || '');
  };

  const handleUpdateReview = async (reviewId) => {
    if (editRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/reviews/${reviewId}`,
        null,
        {
          params: { rating: editRating, review_text: editText },
          withCredentials: true
        }
      );
      toast.success('Review updated!');
      setEditingReview(null);
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        withCredentials: true
      });
      toast.success('Review deleted!');
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete review');
    }
  };

  const handleEnrollFree = async () => {
    setEnrolling(true);
    try {
      await axios.post(`${API}/courses/${id}/enroll-free`, {}, {
        withCredentials: true
      });
      toast.success('Successfully enrolled!');
      setEnrolled(true);
      setTimeout(() => navigate(`/courses/${id}/learn`), 1000);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to enroll');
        // Redirect to login
      } else {
        toast.error(error.response?.data?.detail || 'Failed to enroll');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handlePurchase = async () => {
    setEnrolling(true);
    try {
      const response = await axios.post(
        `${API}/courses/${id}/purchase`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      
      // Redirect to PayPal checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to purchase');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start purchase');
      }
      setEnrolling(false);
    }
  };

  const handleStartCourse = () => {
    navigate(`/courses/${id}/learn`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="cassette-card p-12">
            <p className="text-[#3D2817] text-xl">Course not found</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-16 min-h-screen" data-testid="course-detail-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Course Header */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Course Image */}
            <div className="md:col-span-1">
              {course.cover_image_url ? (
                <img
                  src={course.cover_image_url}
                  alt={course.title}
                  className="w-full rounded-lg shadow-lg"
                  data-testid="course-cover"
                />
              ) : course.display_icon ? (
                <div className="w-full aspect-[3/4] bg-gradient-to-br from-[#E8934D] to-[#6B4423] rounded-lg flex items-center justify-center">
                  {(() => {
                    const iconData = ICONS.find(i => i.name === course.display_icon);
                    const IconComponent = iconData?.icon;
                    return IconComponent ? (
                      <IconComponent className="w-16 h-16 md:w-24 md:h-24 text-[#F4E4C1]" strokeWidth={1.5} />
                    ) : (
                      <BookOpen className="w-16 h-16 md:w-24 md:h-24 text-[#F4E4C1]" />
                    );
                  })()}
                </div>
              ) : (
                <div className="w-full aspect-[3/4] bg-gradient-to-br from-[#E8934D] to-[#6B4423] rounded-lg flex items-center justify-center">
                  <BookOpen className="w-16 h-16 md:w-24 md:h-24 text-[#F4E4C1]" />
                </div>
              )}
            </div>

            {/* Course Info */}
            <div className="md:col-span-2">
              <div className="flex justify-between items-start mb-4">
                <h1 className="text-5xl retro-title text-[#2C1810]" data-testid="course-title" style={{color: '#2C1810', textShadow: '2px 2px 0 #E85D4D, 4px 4px 0 rgba(0,0,0,0.2)'}}>{course.title}</h1>
                <button
                  onClick={toggleWishlist}
                  className={`p-3 rounded-full ${inWishlist ? 'bg-[#E85D4D]' : 'bg-[#F4E4C1]'} hover:scale-110 transition-transform`}
                  title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                >
                  <Heart className={`w-6 h-6 ${inWishlist ? 'text-white fill-white' : 'text-[#E85D4D]'}`} />
                </button>
              </div>
              
              <div className="flex items-center gap-6 mb-4 text-[#6B4423]">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="font-semibold">{course.author}</span>
                </div>
                {course.duration_hours && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{course.duration_hours} hours</span>
                  </div>
                )}
                {course.difficulty && (
                  <span className="px-3 py-1 bg-[#E8934D] text-white rounded-full text-sm font-bold capitalize">
                    {course.difficulty}
                  </span>
                )}
              </div>

              {/* Rating */}
              {reviews.total_reviews > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`text-2xl ${star <= reviews.average_rating ? 'text-[#E8934D]' : 'text-gray-300'}`}>
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="text-[#6B4423] font-bold">{reviews.average_rating}</span>
                  <span className="text-[#6B4423]">({reviews.total_reviews} reviews)</span>
                </div>
              )}

              <p className="text-[#3D2817] text-lg mb-6 leading-relaxed" data-testid="course-description">
                {course.description}
              </p>

              {/* Instructor Bio */}
              {course.author_bio && (
                <div className="cassette-card p-4 mb-6">
                  <div className="flex items-center gap-4">
                    {course.author_image && (
                      <img src={course.author_image} alt={course.author} className="w-16 h-16 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="font-bold text-[#2C1810]">About the Instructor</p>
                      <p className="text-sm text-[#6B4423]">{course.author_bio}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Enrollment Status & Actions */}
              <div className="cassette-card p-6">
                {enrolled ? (
                  <div>
                    <div className="flex items-center gap-2 text-[#1B5E3B] mb-4">
                      <CheckCircle className="w-6 h-6" />
                      <span className="text-xl font-bold">You're enrolled!</span>
                    </div>
                    <button
                      onClick={handleStartCourse}
                      className="retro-button w-full"
                      data-testid="start-course-button"
                    >
                      Continue Learning
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl retro-title text-[#2C1810]">
                        {course.is_free ? 'FREE Course' : `$${course.price}`}
                      </span>
                      {!course.is_free && (
                        <div className="flex items-center gap-2 text-[#6B4423]">
                          <Lock className="w-5 h-5" />
                          <span>Enrollment Required</span>
                        </div>
                      )}
                    </div>

                    {course.is_free ? (
                      <button
                        onClick={handleEnrollFree}
                        disabled={enrolling}
                        className="retro-button w-full"
                        data-testid="enroll-free-button"
                      >
                        {enrolling ? 'Enrolling...' : 'Enroll for Free'}
                      </button>
                    ) : (
                      <button
                        onClick={handlePurchase}
                        disabled={enrolling}
                        className="retro-button w-full"
                        data-testid="purchase-button"
                      >
                        {enrolling ? 'Processing...' : 'Purchase Course'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Course Curriculum - hidden per request */}
          {false && <div className="admin-card">
            <h2 className="text-4xl retro-title mb-8">Course Curriculum</h2>
            
            {course.chapters && course.chapters.length > 0 ? (
              <div className="space-y-4">
                {course.chapters.map((chapter, index) => (
                  <div key={chapter.id} className="cassette-card p-6" data-testid={`chapter-${chapter.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E8934D] flex items-center justify-center text-white font-bold text-xl">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl retro-title text-[#2C1810] mb-2">{chapter.title}</h3>
                        {chapter.description && (
                          <p className="text-[#6B4423]">{chapter.description}</p>
                        )}
                        {!enrolled && (
                          <div className="flex items-center gap-2 text-[#6B4423] mt-2">
                            <Lock className="w-4 h-4" />
                            <span className="text-sm">Enroll to access</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center cassette-card p-12">
                <p className="text-[#6B4423]">Course curriculum coming soon...</p>
              </div>
            )}
          </div>}

          {/* Reviews Section - hidden per request */}
          {false && <div className="admin-card mt-12">
            <h2 className="text-4xl retro-title mb-8">Student Reviews</h2>

            {/* Add Review Button (only if enrolled) */}
            {enrolled && (
              <div className="mb-8">
                {!showReviewForm ? (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="retro-button"
                  >
                    Write a Review
                  </button>
                ) : (
                  <div className="cassette-card p-6">
                    <h3 className="text-xl retro-title text-[#2C1810] mb-4">Your Review</h3>
                    
                    <div className="mb-4">
                      <p className="text-[#6B4423] mb-2">Rating:</p>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setUserRating(star)}
                            className="text-4xl hover:scale-110 transition-transform"
                          >
                            <span className={star <= userRating ? 'text-[#E8934D]' : 'text-gray-300'}>★</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <textarea
                      placeholder="Share your experience with this course..."
                      value={userReview}
                      onChange={(e) => setUserReview(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-[#F4E4C1] text-[#3D2817] mb-4"
                    />

                    <div className="flex gap-2">
                      <button onClick={submitReview} className="retro-button">
                        Submit Review
                      </button>
                      <button
                        onClick={() => {
                          setShowReviewForm(false);
                          setUserRating(0);
                          setUserReview('');
                        }}
                        className="retro-button-small"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews List */}
            {reviews.reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.reviews.map((review) => (
                  <div key={review.id} className="cassette-card p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E8934D] flex items-center justify-center text-white font-bold">
                        {review.user_name?.charAt(0) || 'U'}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-bold text-[#2C1810]">{review.user_name}</p>
                            {editingReview === review.id ? (
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onClick={() => setEditRating(star)}
                                    className={`text-2xl ${star <= editRating ? 'text-[#E8934D]' : 'text-gray-300'}`}
                                  >
                                    ★
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span key={star} className={`text-lg ${star <= review.rating ? 'text-[#E8934D]' : 'text-gray-300'}`}>
                                    ★
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#6B4423]">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                            {isAdmin && (
                              <div className="flex gap-1">
                                {editingReview === review.id ? (
                                  <>
                                    <button
                                      onClick={() => handleUpdateReview(review.id)}
                                      className="text-[#4D9E7C] hover:text-[#3D7E5C] p-1"
                                      title="Save"
                                    >
                                      <CheckCircle className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingReview(null)}
                                      className="text-[#6B4423] hover:text-[#8B6F47] p-1"
                                      title="Cancel"
                                    >
                                      ✕
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEditReview(review)}
                                      className="text-[#6B4423] hover:text-[#8B6F47] p-1"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteReview(review.id)}
                                      className="text-[#E85D4D] hover:text-[#C84D3D] p-1"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {editingReview === review.id ? (
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-[#F4E4C1] text-[#3D2817] mt-2"
                            rows="3"
                            placeholder="Review text (optional)"
                          />
                        ) : (
                          review.review_text && (
                            <p className="text-[#3D2817]">{review.review_text}</p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center cassette-card p-12">
                <p className="text-[#6B4423]">No reviews yet. Be the first to review this course!</p>
              </div>
            )}
          </div>}

        </div>
      </section>
    </Layout>
  );
}
